import Vue from 'vue';
import api from '../../api';
import _ from 'lodash';
import router from '../../router';
import { dispatchesQueries, mutatesQueries, mutatesSchema, updatePagination } from '../helper';

const default_query = {
  order_by: 'created_at',
  dir: 'desc',
};

const emptyPagination = null;

const initialSchema = {
  property_type: {
    id: 'property_type',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.property_type_room',
        value: 'room',
        text: 'Rooms to Rent',
      },
      {
        id: 'advert.property_type_apartment',
        value: 'apartment',
        text: 'Whole Property',
      },
    ],
    title: 'Property type',
    group: 'advert.group_property_select',
    required: true,
    update: true,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  room_amount: {
    id: 'room_amount',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'integer',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.room_amount_1',
        value: 1,
        text: '1 room for rent',
      },
      {
        id: 'advert.room_amount_2',
        value: 2,
        text: '2 rooms for rent',
      },
      {
        id: 'advert.room_amount_3',
        value: 3,
        text: '3 rooms for rent',
      },
      {
        id: 'advert.room_amount_4',
        value: 4,
        text: '4 rooms for rent',
      },
      {
        id: 'advert.room_amount_5',
        value: 5,
        text: '5 rooms for rent',
      },
      {
        id: 'advert.room_amount_6',
        value: 6,
        text: '6 rooms for rent',
      },
      {
        id: 'advert.room_amount_7',
        value: 7,
        text: '7 rooms for rent',
      },
      {
        id: 'advert.room_amount_8',
        value: 8,
        text: '8 rooms for rent',
      },
      {
        id: 'advert.room_amount_9',
        value: 9,
        text: '9+ rooms for rent',
      },
    ],
    title: 'I have',
    group: 'advert.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  occupants_max: {
    id: 'occupants_max',
    enabled: true,
    validation: ['required'],
    default: 1,
    type: 'integer',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.occupants_max_0',
        value: 0,
        text: 'Select',
      },
      {
        id: 'advert.occupants_max_1',
        value: 1,
        text: '1 person',
      },
      {
        id: 'advert.occupants_max_2',
        value: 2,
        text: '2 persons',
      },
      {
        id: 'advert.occupants_max_3',
        value: 3,
        text: '3 persons',
      },
      {
        id: 'advert.occupants_max_4',
        value: 4,
        text: '4 persons',
      },
      {
        id: 'advert.occupants_max_5',
        value: 5,
        text: '5 persons',
      },
      {
        id: 'advert.occupants_max_6',
        value: 6,
        text: '6 persons',
      },
      {
        id: 'advert.occupants_max_7',
        value: 7,
        text: '7 persons',
      },
      {
        id: 'advert.occupants_max_8',
        value: 8,
        text: '8 persons',
      },
      {
        id: 'advert.occupants_max_9',
        value: 9,
        text: '9+ persons',
      },
    ],
    title: 'Size of property',
    group: 'advert.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  occupants_amount: {
    id: 'occupants_amount',
    enabled: false,
    validation: ['required', 'lt:occupants_max'],
    default: 0,
    type: 'integer',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.occupants_amount_0',
        value: 0,
        text: 'No occupants yet',
      },
      {
        id: 'advert.occupants_amount_1',
        value: 1,
        text: '1 person',
      },
      {
        id: 'advert.occupants_amount_2',
        value: 2,
        text: '2 persons',
      },
      {
        id: 'advert.occupants_amount_3',
        value: 3,
        text: '3 persons',
      },
      {
        id: 'advert.occupants_amount_4',
        value: 4,
        text: '4 persons',
      },
      {
        id: 'advert.occupants_amount_5',
        value: 5,
        text: '5 persons',
      },
      {
        id: 'advert.occupants_amount_6',
        value: 6,
        text: '6 persons',
      },
      {
        id: 'advert.occupants_amount_7',
        value: 7,
        text: '7 persons',
      },
      {
        id: 'advert.occupants_amount_8',
        value: 8,
        text: '8 persons',
      },
      {
        id: 'advert.occupants_amount_9',
        value: 9,
        text: '9+ persons',
      },
    ],
    title: 'There are already',
    group: 'advert.group_get_started',
    required: true,
    update: true,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  owner_type: {
    id: 'owner_type',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'string',
    presentation: 'radio',
    is_dynamic: false,
    options: [
      {
        id: 'advert.owner_type_landlord',
        value: 'landlord',
        text: 'Landlord',
      },
      {
        id: 'advert.owner_type_tenant',
        value: 'tenant',
        text: 'Tenant/Flatmate',
      },
      {
        id: 'advert.owner_type_agent',
        value: 'agent',
        text: 'Agent',
      },
    ],
    title: 'I am a',
    group: 'advert.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  price_value: {
    id: 'price_value',
    enabled: true,
    validation: ['required', 'not_in:null', 'numeric', 'min:1', 'max:999999'],
    default: null,
    type: 'float',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Cost',
    group: 'advert.group_about_property',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  price_currency: {
    id: 'price_currency',
    enabled: true,
    validation: null,
    default: 'usd',
    type: 'string',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'Currency',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  rent_period: {
    id: 'rent_period',
    enabled: true,
    validation: null,
    default: 'month',
    type: 'string',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'Period',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  security_deposit: {
    id: 'security_deposit',
    enabled: true,
    validation: ['numeric', 'min:0', 'max:999999'],
    default: null,
    type: 'float',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Security deposit',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  area: {
    id: 'area',
    enabled: true,
    validation: ['numeric', 'min:1', 'max:1000'],
    default: null,
    type: 'float',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Area',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  area_unit: {
    id: 'area_unit',
    enabled: true,
    validation: null,
    default: 'm2',
    type: 'string',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'Unit',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  location: {
    id: 'location',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'location',
    presentation: 'location',
    is_dynamic: false,
    options: null,
    title: 'Located in',
    group: 'advert.group_about_property',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  transport_mode: {
    id: 'transport_mode',
    enabled: true,
    validation: null,
    default: 'walk',
    type: 'string',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'Transport',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  transport_duration: {
    id: 'transport_duration',
    enabled: true,
    validation: ['nullable', 'numeric'],
    default: null,
    type: 'integer',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.transport_duration_select',
        value: null,
        text: 'Select',
      },
      {
        id: 'advert.transport_duration_5',
        value: 5,
        text: '5',
      },
      {
        id: 'advert.transport_duration_10',
        value: 10,
        text: '10',
      },
      {
        id: 'advert.transport_duration_15',
        value: 15,
        text: '15',
      },
      {
        id: 'advert.transport_duration_20',
        value: 20,
        text: '20',
      },
      {
        id: 'advert.transport_duration_30',
        value: 30,
        text: '30+',
      },
    ],
    title: '',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  transport_from: {
    id: 'transport_from',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.transport_from_select',
        value: null,
        text: 'Select the type of transport',
      },
      {
        id: 'advert.transport_from_bus',
        value: 'bus',
        text: 'Bus stop',
      },
      {
        id: 'advert.transport_from_tram',
        value: 'tram',
        text: 'Tram stop',
      },
      {
        id: 'advert.transport_from_metro',
        value: 'metro',
        text: 'Metro',
      },
    ],
    title: 'from',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  transport_line: {
    id: 'transport_line',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'text',
    is_dynamic: false,
    options: null,
    title: 'Lines',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  living_room: {
    id: 'living_room',
    enabled: true,
    validation: null,
    default: true,
    type: 'boolean',
    presentation: 'radio',
    is_dynamic: false,
    options: null,
    title: 'Living room?',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  amenities: {
    id: 'amenities',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'array',
    presentation: 'checkbox',
    is_dynamic: false,
    options: [
      {
        id: 'advert.amenities_air_conditioning',
        value: 'air_conditioning',
        text: 'Air conditioning',
      },
      {
        id: 'advert.amenities_barbecue_area',
        value: 'barbecue_area',
        text: 'Barbecue area',
      },
      {
        id: 'advert.amenities_dishwasher',
        value: 'dishwasher',
        text: 'Dishwasher',
      },
      {
        id: 'advert.amenities_gym',
        value: 'gym',
        text: 'Gym',
      },
      {
        id: 'advert.amenities_laundry',
        value: 'laundry',
        text: 'Laundry',
      },
      {
        id: 'advert.amenities_microwave',
        value: 'microwave',
        text: 'Microwave',
      },
      {
        id: 'advert.amenities_sauna',
        value: 'sauna',
        text: 'Sauna',
      },
      {
        id: 'advert.amenities_swimming_pool',
        value: 'swimming_pool',
        text: 'Swimming pool',
      },
      {
        id: 'advert.amenities_tv_cable',
        value: 'tv_cable',
        text: 'TV Cable',
      },
      {
        id: 'advert.amenities_wi_fi',
        value: 'wi_fi',
        text: 'Wi-Fi',
      },
    ],
    title: 'Amenities',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  facilities: {
    id: 'facilities',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'array',
    presentation: 'checkbox',
    is_dynamic: false,
    options: [
      {
        id: 'advert.facilities_beachside',
        value: 'beachside',
        text: 'Beachside',
      },
      {
        id: 'advert.facilities_farmacy',
        value: 'farmacy',
        text: 'Farmacy',
      },
      {
        id: 'advert.facilities_free_parking',
        value: 'free_parking',
        text: 'Free parking',
      },
      {
        id: 'advert.facilities_markets',
        value: 'markets',
        text: 'Markets',
      },
      {
        id: 'advert.facilities_playground',
        value: 'playground',
        text: 'Playground',
      },
      {
        id: 'advert.facilities_reception',
        value: 'reception',
        text: 'Reception',
      },
      {
        id: 'advert.facilities_security',
        value: 'security',
        text: 'Security',
      },
      {
        id: 'advert.facilities_parking',
        value: 'parking',
        text: 'Parking',
      },
      {
        id: 'advert.facilities_garage',
        value: 'garage',
        text: 'Garage',
      },
      {
        id: 'advert.facilities_garden',
        value: 'garden',
        text: 'Garden',
      },
    ],
    title: 'Facilities',
    group: 'advert.group_about_property',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  available_from: {
    id: 'available_from',
    enabled: true,
    validation: ['required'],
    default: '2019-10-18',
    type: 'date',
    presentation: 'date',
    is_dynamic: false,
    options: null,
    title: 'Available from',
    group: 'advert.group_about_property',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  room_size: {
    id: 'room_size',
    enabled: false,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'radio',
    is_dynamic: true,
    options: [
      {
        id: 'advert.room_size_single',
        value: 'single',
        text: 'Single',
      },
      {
        id: 'advert.room_size_double',
        value: 'double',
        text: 'Double',
      },
    ],
    title: 'Size of room',
    group: 'advert.group_about_room',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  bills_included: {
    id: 'bills_included',
    enabled: true,
    validation: ['required'],
    default: 'yes',
    type: 'string',
    presentation: 'radio',
    is_dynamic: false,
    options: [
      {
        id: 'advert.bills_included_yes',
        value: 'yes',
        text: 'Included',
      },
      {
        id: 'advert.bills_included_no',
        value: 'no',
        text: 'Not included',
      },
      {
        id: 'advert.bills_included_some',
        value: 'some',
        text: 'Some included',
      },
    ],
    title: 'Bills',
    group: 'advert.group_about_room',
    required: true,
    update: true,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  est_bill_amount: {
    id: 'est_bill_amount',
    enabled: true,
    validation: ['numeric', 'min:0', 'max:999999', 'required_if:bills_included,no,some'],
    default: null,
    type: 'float',
    presentation: 'number',
    is_dynamic: true,
    options: null,
    title: 'Estimated bills',
    group: 'advert.group_about_room',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  broadband_included: {
    id: 'broadband_included',
    enabled: false,
    validation: null,
    default: true,
    type: 'boolean',
    presentation: 'radio',
    is_dynamic: true,
    options: [
      {
        id: 'advert.broadband_included_true',
        value: true,
        text: 'Yes',
      },
      {
        id: 'advert.broadband_included_false',
        value: false,
        text: 'No',
      },
    ],
    title: 'Broadband included',
    group: 'advert.group_about_room',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_smoking: {
    id: 'tenant_smoking',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_smoking_any',
        value: null,
        text: 'No preference',
      },
      {
        id: 'advert.tenant_smoking_yes',
        value: 'yes',
        text: 'Yes',
      },
      {
        id: 'advert.tenant_smoking_no',
        value: 'no',
        text: 'No',
      },
    ],
    title: 'Smoking',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_occupation: {
    id: 'tenant_occupation',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_occupation_any',
        value: null,
        text: 'No preference',
      },
      {
        id: 'advert.tenant_occupation_student',
        value: 'student',
        text: 'Student',
      },
      {
        id: 'advert.tenant_occupation_professional',
        value: 'professional',
        text: 'Professional',
      },
    ],
    title: 'Occupation',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_pets: {
    id: 'tenant_pets',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_pets_any',
        value: null,
        text: 'No preference',
      },
      {
        id: 'advert.tenant_pets_yes',
        value: 'yes',
        text: 'Yes',
      },
      {
        id: 'advert.tenant_pets_no',
        value: 'no',
        text: 'No',
      },
    ],
    title: 'Pets considered',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_gender: {
    id: 'tenant_gender',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_gender_any',
        value: null,
        text: 'No preference',
      },
      {
        id: 'advert.tenant_gender_male',
        value: 'male',
        text: 'Male',
      },
      {
        id: 'advert.tenant_gender_female',
        value: 'female',
        text: 'Female',
      },
      {
        id: 'advert.tenant_gender_mixed',
        value: 'mixed',
        text: 'Mixed',
      },
    ],
    title: 'Gender',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_age_min: {
    id: 'tenant_age_min',
    enabled: true,
    validation: ['numeric', 'min:18', 'max:99', 'nullable'],
    default: 18,
    type: 'integer',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Minimal age of tenant',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_age_max: {
    id: 'tenant_age_max',
    enabled: true,
    validation: ['numeric', 'min:18', 'max:99', 'gte:tenant_age_min', 'nullable'],
    default: 99,
    type: 'integer',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Maximal age of tenant',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_language: {
    id: 'tenant_language',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_language_any',
        value: null,
        text: "Doesn't matter",
      },
      {
        id: 'advert.tenant_language_polish',
        value: 'polish',
        text: 'Polish',
      },
      {
        id: 'advert.tenant_language_english',
        value: 'english',
        text: 'English',
      },
      {
        id: 'advert.tenant_language_russian',
        value: 'russian',
        text: 'Russian',
      },
      {
        id: 'advert.tenant_language_ukrainian',
        value: 'ukrainian',
        text: 'Ukrainian',
      },
      {
        id: 'advert.tenant_language_german',
        value: 'german',
        text: 'German',
      },
      {
        id: 'advert.tenant_language_spanish',
        value: 'spanish',
        text: 'Spanish',
      },
      {
        id: 'advert.tenant_language_other',
        value: 'other',
        text: 'Other',
      },
    ],
    title: 'Language',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_couples: {
    id: 'tenant_couples',
    enabled: true,
    validation: null,
    default: true,
    type: 'boolean',
    presentation: 'radio',
    is_dynamic: false,
    options: [
      {
        id: 'advert.tenant_couples_true',
        value: true,
        text: 'Yes',
      },
      {
        id: 'advert.tenant_couples_false',
        value: false,
        text: 'No',
      },
    ],
    title: 'Couples welcome?',
    group: 'advert.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_smoking: {
    id: 'flatmate_smoking',
    enabled: false,
    validation: null,
    default: 'no',
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_smoking_yes',
        value: 'yes',
        text: 'Yes',
      },
      {
        id: 'advert.flatmate_smoking_no',
        value: 'no',
        text: 'No',
      },
    ],
    title: 'Smoking',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_gender: {
    id: 'flatmate_gender',
    enabled: false,
    validation: null,
    default: 'mixed',
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_gender_male',
        value: 'male',
        text: 'Male',
      },
      {
        id: 'advert.flatmate_gender_female',
        value: 'female',
        text: 'Female',
      },
      {
        id: 'advert.flatmate_gender_mixed',
        value: 'mixed',
        text: 'Mixed',
      },
    ],
    title: 'Gender',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_occupation: {
    id: 'flatmate_occupation',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_occupation_not_disclosed',
        value: null,
        text: 'Not disclosed',
      },
      {
        id: 'advert.flatmate_occupation_student',
        value: 'student',
        text: 'Student',
      },
      {
        id: 'advert.flatmate_occupation_professional',
        value: 'professional',
        text: 'Professional',
      },
      {
        id: 'advert.flatmate_occupation_other',
        value: 'other',
        text: 'Other',
      },
      {
        id: 'advert.flatmate_occupation_mixed',
        value: 'mixed',
        text: 'Mixed',
      },
    ],
    title: 'Occupation',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_pets: {
    id: 'flatmate_pets',
    enabled: false,
    validation: ['nullable'],
    default: 'no',
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_pets_no',
        value: 'no',
        text: 'No',
      },
      {
        id: 'advert.flatmate_pets_yes',
        value: 'yes',
        text: 'Yes',
      },
    ],
    title: 'Have pets?',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_age: {
    id: 'flatmate_age',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'integer',
    presentation: 'number',
    is_dynamic: true,
    options: null,
    title: 'Age',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_language: {
    id: 'flatmate_language',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_language_any',
        value: null,
        text: 'Not disclosed',
      },
      {
        id: 'advert.flatmate_language_polish',
        value: 'polish',
        text: 'Polish',
      },
      {
        id: 'advert.flatmate_language_english',
        value: 'english',
        text: 'English',
      },
      {
        id: 'advert.flatmate_language_russian',
        value: 'russian',
        text: 'Russian',
      },
      {
        id: 'advert.flatmate_language_ukrainian',
        value: 'ukrainian',
        text: 'Ukrainian',
      },
      {
        id: 'advert.flatmate_language_german',
        value: 'german',
        text: 'German',
      },
      {
        id: 'advert.flatmate_language_spanish',
        value: 'spanish',
        text: 'Spanish',
      },
      {
        id: 'advert.flatmate_language_other',
        value: 'other',
        text: 'Other',
      },
    ],
    title: 'Language',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_nationality: {
    id: 'flatmate_nationality',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      {
        id: 'advert.flatmate_nationality_any',
        value: null,
        text: 'Not disclosed',
      },
      {
        id: 'advert.flatmate_nationality_polish',
        value: 'polish',
        text: 'Polish',
      },
      {
        id: 'advert.flatmate_nationality_english',
        value: 'english',
        text: 'English',
      },
      {
        id: 'advert.flatmate_nationality_russian',
        value: 'russian',
        text: 'Russian',
      },
      {
        id: 'advert.flatmate_nationality_ukrainian',
        value: 'ukrainian',
        text: 'Ukrainian',
      },
      {
        id: 'advert.flatmate_nationality_german',
        value: 'german',
        text: 'German',
      },
      {
        id: 'advert.flatmate_nationality_spanish',
        value: 'spanish',
        text: 'Spanish',
      },
      {
        id: 'advert.flatmate_nationality_other',
        value: 'other',
        text: 'Other',
      },
    ],
    title: 'Nationality',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_interests: {
    id: 'flatmate_interests',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'text',
    is_dynamic: true,
    options: null,
    title: 'Interests',
    group: 'advert.group_existing_flatmate',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  title: {
    id: 'title',
    enabled: true,
    validation: ['required', 'min:10', 'max:120'],
    default: null,
    type: 'string',
    presentation: 'text',
    is_dynamic: false,
    options: null,
    title: 'Advert title',
    group: 'advert.group_advert_details',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  description: {
    id: 'description',
    enabled: true,
    validation: ['required', 'min:100', 'max:1000'],
    default: null,
    type: 'string',
    presentation: 'textarea',
    is_dynamic: false,
    options: null,
    title: 'Description',
    group: 'advert.group_advert_details',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  images: {
    id: 'images',
    enabled: true,
    validation: ['nullable'],
    default: null,
    type: 'array',
    presentation: 'images',
    is_dynamic: false,
    options: null,
    title: 'Upload photos',
    group: 'advert.group_advert_details',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  use_different_profile: {
    id: 'use_different_profile',
    enabled: true,
    validation: null,
    default: false,
    type: 'boolean',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: "I don't own property, so I leave the contact details of the owner.",
    group: 'advert.group_advert_details',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  different_profile: {
    id: 'different_profile',
    enabled: true,
    validation: null,
    default: false,
    type: 'array',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'advert.different_profile',
    group: 'advert.group_advert_details',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
};

const state = () => ({
  query: default_query,
  schema: initialSchema,
  instance: null,
  // instance_loaded: false,
  item: null,
  pagination: emptyPagination,
  progress: {
    fetch: false,
    load: false,
    show: false,
    store: false,
    delete: false,
    recover: false,
    schema: false,
  },

  // B
  fetching: false,
  fetched: false,
  // R
  instance_loading: false,
  instance_loaded: false,
  // E-A
  instance_saving: false,
  instance_saved: false,
  // D
  instance_deleting: false,
  instance_deleted: false,
  // Additional
  instance_publishing: false,
  instance_published: false,
  instance_reporting: false,
  instance_reported: false,
});

const actions = {
  ...dispatchesQueries(default_query),

  fetch: function(context) {
    const query = context.state.query;
    const promise = api.advert.my(query);
    context.commit('handleStatus', { fetching: true });
    promise
      .then((response) => {
        context.commit('handlePaginationResults', response.data);
        context.commit('handleStatus', { fetched: true });
      })
      .catch((error) => {
        context.commit('handleStatus', { fetched: false });
      })
      .then(() => {
        context.commit('handleStatus', { fetching: false });
      });
  },

  show: function(context, uuid) {
    const promise = api.advert.show(uuid);
    context.commit('handleStatus', { instance_loading: true });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_loaded: true });
        context.commit('handleShow', response.data);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_loaded: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_loading: false });
      });
  },

  create: function(context) {
    const promise = api.advert.create(context.state.instance);
    context.commit('handleStatus', { instance_loading: true });
    // ++
    context.commit('handleStatus', { instance_saved: false });
    promise
      .then((response) => {
        context.commit('handleSchemaLoaded', response.data);
        context.commit('handleInititalizingInstanceFromSchema', response.data);
        context.commit('handleStatus', { instance_loaded: true });
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_loaded: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_loading: false });
      });
  },

  edit: function(context, uuid) {
    const promise = api.advert.edit(uuid, context.state.instance);
    context.commit('handleStatus', { instance_loading: true });
    // ++
    context.commit('handleStatus', { instance_saved: false });
    promise
      .then((response) => {
        context.commit('handleSchemaLoaded', response.data);
        context.commit('handleInititalizingInstanceFromSchema', response.data);
        context.commit('handleStatus', { instance_loaded: true });
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_loaded: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_loading: false });
      });
  },

  store: function(context) {
    const promise = api.advert.store(context.state.instance);
    context.commit('handleStatus', { instance_saving: true });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_saved: true });
        context.commit('handleResetInstance', {});
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_saved: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_saving: false });
      });
  },

  update: function(context, uuid) {
    const promise = api.advert.update(uuid, context.state.instance);
    context.commit('handleStatus', { instance_saving: true });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_saved: true });
        context.commit('handleResetInstance', {});
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_saved: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_saving: false });
      });
  },

  delete: function(context, uuid) {
    const promise = api.advert.delete(uuid);
    context.commit('handleStatus', { instance_deleting: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_deleted: true });
        context.commit('handleInstanceDeleted', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_deleted: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_deleting: false });
      });
  },

  publish: function(context, uuid) {
    const promise = api.advert.publish(uuid);
    context.commit('handleStatus', { instance_publishing: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_published: true });
        context.commit('handleInstancePublished', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_published: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_publishing: false });
      });
  },

  unpublish: function(context, uuid) {
    const promise = api.advert.unpublish(uuid);
    context.commit('handleStatus', { instance_publishing: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_published: true });
        context.commit('handleInstanceUnpublished', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_published: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_publishing: false });
      });
  },

  report: function(context, uuid) {
    const promise = api.advert.unavailable(uuid);
    context.commit('handleStatus', { instance_reporting: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_reported: uuid });
      })
      .catch((error) => {
        if (error.response.status == 422) {
          context.commit(
            'errors/put',
            {
              key: 'error',
              value: 'You have already reported this advert.',
            },
            { root: true }
          );
          context.commit('handleStatus', { instance_reported: uuid });
        } else {
          context.commit('handleStatus', { instance_reported: false });
        }
      })
      .then(() => {
        context.commit('handleStatus', { instance_reporting: false });
      });
  },

  recover: function(context) {
    // @todo ...
  },

  cancel: function(context, payload) {
    const target = _.get(payload, 'target', _.get(this.state, 'target'));
    context.commit('handleCancel', { target: target });
  },

  updateInstance: function(context, payload) {
    // console.log(JSON.stringify(payload, null, 2));
    context.commit('handleUpdateInstance', payload);
  },

  resetInstance: function(context, payload) {
    context.commit('handleResetInstance', payload);
  },
};

const mutations = {
  ...mutatesQueries(),
  ...mutatesSchema(),

  handleLoadingStatus: function(state, { action, status }) {
    _.set(state, `progress.${action}`, status);
  },

  handleShow: function(state, data) {
    _.set(state, `instance`, data);
  },

  handlePaginationResults: function(state, data) {
    _.set(state, `pagination`, data);
  },

  handleCancel: function(state) {
    _.set(state, `item`, null);
  },

  handleEdit: function(state, data) {
    _.set(state, `item`, {});
    _.set(state, `item`, data);

    _.each(data, (config, k) => {
      let key = config.id;
      let default_value = _.get(config, 'default', null);
      let current_value = _.get(config, 'value', default_value);
      current_value = current_value != null ? current_value : default_value;
      _.set(state, `instance.${key}`, current_value);
    });
  },

  handleUpdateInstance: function(state, payload) {
    let instance = _.cloneDeep(state.instance);
    _.each(payload, (value, key) => {
      if (value == null) {
        _.unset(instance, key);
      } else {
        _.set(instance, key, value);
      }
    });
    state.instance = instance;

    // @todo
    // Not the most elegant solution to the problem,
    // but for some reason Vuex stopped seeing changes in input
    //
    // @acthung !!! types!!!
    let schema = Object.assign({}, state.schema);
    _.each(payload, (value, key) => {
      if (_.has(schema, key)) {
        _.set(schema, `${key}.value`, value);
      }
    });
    state.schema = schema;
  },

  handleInstanceDeleted: function(state, uuid) {
    updatePagination(state, uuid, () => null);
  },

  handleInstancePublished: function(state, uuid) {
    updatePagination(state, uuid, (record) => {
      record.status = 'published';
      return record;
    });
  },

  handleInstanceUnpublished: function(state, uuid) {
    updatePagination(state, uuid, (record) => {
      record.status = 'unpublished';
      return record;
    });
  },

  handleResetInstance: function(state, payload) {
    _.set(state, 'instance', {});
  },

  handleInstanceLoaded: function(state, status) {
    _.set(state, 'instance_loaded', status);
  },

  handleInstanceSaved: function(state, payload) {
    // does nothing, just for observing...
  },

  handleInstanceFailed: function(state, payload) {
    // does nothing, just for observing...
  },

  handleResetInstance: function(state, payload) {
    _.set(state, 'instance', {});
  },

  handleStatus: function(state, payload) {
    _.each(payload, (value, key) => {
      _.set(state, key, value);
    });
  },

  handleInititalizingInstanceFromSchema: function(state, payload) {
    let new_insance = {};
    _.each(payload, (config, k) => {
      let key = config.id;
      let default_value = _.get(config, 'default', null);
      let current_value = _.get(config, 'value', default_value);
      current_value = current_value != null ? current_value : default_value;
      _.set(new_insance, key, current_value);
    });
    state.instance = new_insance;
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
